const THEME_COOKIE_KEY = 'sair-theme-mode';
const SUPPORTED_MODES = new Set(['system', 'light', 'dark']);
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 2; // ~2 years
const SECOND_LEVEL_TLDS = ['co.uk', 'org.uk', 'gov.uk', 'ac.uk', 'com.au', 'net.au', 'org.au'];

const isIpAddress = (host) => /^(\d{1,3}\.){3}\d{1,3}$/.test(host);

const getCookieDomain = () => {
    const { hostname } = window.location;
    if (!hostname || hostname === 'localhost' || isIpAddress(hostname)) {
        return undefined;
    }

    const parts = hostname.split('.');
    if (parts.length <= 1) {
        return undefined;
    }

    const lastTwo = parts.slice(-2).join('.');
    const lastThree = parts.slice(-3).join('.');

    if (SECOND_LEVEL_TLDS.includes(lastTwo) && parts.length >= 3) {
        return `.${lastThree}`;
    }

    return `.${lastTwo}`;
};

const readThemeModeCookie = () => {
    const cookie = document.cookie
        .split('; ')
        .find((item) => item.startsWith(`${THEME_COOKIE_KEY}=`))
        ?.split('=')[1];

    if (!cookie) return undefined;

    try {
        const value = decodeURIComponent(cookie);
        return SUPPORTED_MODES.has(value) ? value : undefined;
    } catch (error) {
        console.warn('Failed to parse theme cookie', error);
        return undefined;
    }
};

const writeThemeModeCookie = (mode) => {
    const segments = [`${THEME_COOKIE_KEY}=${encodeURIComponent(mode)}`, `Max-Age=${COOKIE_MAX_AGE_SECONDS}`, 'Path=/'];

    const domain = getCookieDomain();
    if (domain) {
        segments.push(`Domain=${domain}`);
    }

    if (window.location.protocol === 'https:') {
        segments.push('Secure');
    }

    segments.push('SameSite=Lax');

    document.cookie = segments.join('; ');
};

const getSystemPreference = () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

const applyThemeClass = (themeName) => {
    if (!document?.body) return;
    document.body.classList.remove('theme-light', 'theme-dark');
    document.body.classList.add(`theme-${themeName}`);
};

const resolveTheme = (mode, systemPrefersDark) => (mode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : mode);

const getToggleButtons = () => Array.from(document.querySelectorAll('[data-theme-toggle]'));

const syncToggleState = (buttons, activeMode) => {
    if (!buttons.length) return;
    buttons.forEach((button) => {
        const buttonMode = button.getAttribute('data-theme-mode');
        const isActive = buttonMode === activeMode;
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
};

const attachToggleHandlers = (buttons, setMode) => {
    if (!buttons.length) return;
    buttons.forEach((button) => {
        button.addEventListener('click', () => {
            const requestedMode = button.getAttribute('data-theme-mode');
            if (requestedMode && SUPPORTED_MODES.has(requestedMode)) {
                setMode(requestedMode);
            }
        });
    });
};

const getThemeLogos = () => Array.from(document.querySelectorAll('[data-theme-logo-light][data-theme-logo-dark]'));

const syncThemeLogos = (logos, resolvedTheme) => {
    if (!logos.length) {
        return;
    }
    logos.forEach((logo) => {
        const nextSrc =
            resolvedTheme === 'dark'
                ? logo.getAttribute('data-theme-logo-dark') || logo.getAttribute('data-theme-logo-light')
                : logo.getAttribute('data-theme-logo-light') || logo.getAttribute('data-theme-logo-dark');
        if (nextSrc && logo.getAttribute('src') !== nextSrc) {
            logo.setAttribute('src', nextSrc);
        }
    });
};

const getThemeMediaNodes = () => Array.from(document.querySelectorAll('[data-theme-src-light],[data-theme-srcset-light]'));

const syncThemeMediaNodes = (nodes, resolvedTheme) => {
    if (!nodes.length) {
        return;
    }
    nodes.forEach((node) => {
        updateThemeAttribute(node, 'src', resolvedTheme);
        updateThemeAttribute(node, 'srcset', resolvedTheme);
    });
};

const updateThemeAttribute = (node, attr, resolvedTheme) => {
    const lightValue = node.getAttribute(`data-theme-${attr}-light`);
    const darkValue = node.getAttribute(`data-theme-${attr}-dark`);
    if (!lightValue && !darkValue) {
        return;
    }
    const nextValue = resolvedTheme === 'dark' ? darkValue || lightValue : lightValue || darkValue;
    if (nextValue && node.getAttribute(attr) !== nextValue) {
        node.setAttribute(attr, nextValue);
    }
};

const initThemePreference = () => {
    let mode = readThemeModeCookie() || 'system';
    let systemPrefersDark = getSystemPreference();
    const toggleButtons = getToggleButtons();
    const themeLogos = getThemeLogos();
    const themeMediaNodes = getThemeMediaNodes();

    const updateTheme = () => {
        const resolved = resolveTheme(mode, systemPrefersDark);
        applyThemeClass(resolved);
        syncToggleState(toggleButtons, mode);
        syncThemeLogos(themeLogos, resolved);
        syncThemeMediaNodes(themeMediaNodes, resolved);
    };

    const setMode = (nextMode) => {
        if (!SUPPORTED_MODES.has(nextMode)) {
            return;
        }
        mode = nextMode;
        writeThemeModeCookie(mode);
        updateTheme();
    };

    const mediaQuery = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : undefined;
    if (mediaQuery) {
        const handleChange = (event) => {
            systemPrefersDark = event.matches;
            if (mode === 'system') {
                updateTheme();
            }
        };
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange);
        } else if (mediaQuery.addListener) {
            mediaQuery.addListener(handleChange);
        }
    }

    updateTheme();
    attachToggleHandlers(toggleButtons, setMode);

    window['SAIRThemePreference'] = {
        getMode: () => mode,
        setMode,
        getResolvedTheme: () => resolveTheme(mode, systemPrefersDark),
    };
};

export default initThemePreference;
